import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, MapPin, Sparkles } from "lucide-react";

import { person, stats } from "../data/profile";
import { Marquee, TiltCard } from "./_shared";
import { scrollState } from "../three/scrollStore";
import portrait from "../assets/ss3.jpg";
import { useResumeUrl } from "../lib/resumeStore";

const HEADLINE_STACK = [
  "React.js",
  "Next.js",
  "TypeScript",
  "Node.js",
  "Express.js",
  "MongoDB",
  "Three.js",
  "OpenAI API",
  "MCP",
  "Tailwind CSS",
  "Redux",
  "CI/CD",
];

/* A rotating noun so the headline says several true things over time
   without needing three separate headlines. */
const ROLES = [
  "multi-tenant platforms",
  "production AI features",
  "3D web experiences",
  "REST API architecture",
  "engineering teams",
];

function RotatingWord() {
  const [i, setI] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setI((v) => (v + 1) % ROLES.length), 2600);
    return () => clearInterval(id);
  }, [reduce]);

  if (reduce) return <span className="gradient-text">{ROLES[0]}</span>;

  return (
    <span className="relative inline-grid align-bottom">
      {ROLES.map((r, idx) => (
        <motion.span
          key={r}
          aria-hidden={idx !== i}
          /* Below sm the longest phrase ("REST API architecture") is wider
             than a 320px viewport, so let it wrap there. The inline-grid
             still sizes to the tallest variant, so nothing shifts as the
             word rotates. */
          className="gradient-text col-start-1 row-start-1 whitespace-normal sm:whitespace-nowrap"
          initial={false}
          animate={
            idx === i
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : { opacity: 0, y: idx < i ? -14 : 14, filter: "blur(6px)" }
          }
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {r}
        </motion.span>
      ))}
    </span>
  );
}

/** Scroll cue that fades out the moment the user starts scrolling. */
function ScrollCue() {
  const ref = useRef(null);

  useEffect(() => {
    let raf;
    const loop = () => {
      if (ref.current) {
        const o = Math.max(0, 1 - scrollState.y / 260);
        ref.current.style.opacity = String(o);
        ref.current.style.transform = `translateY(${(1 - o) * 16}px)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
    >
      <span className="font-mono text-[0.6rem] uppercase tracking-label text-slate-600">
        Scroll
      </span>
      <span className="relative h-12 w-px overflow-hidden bg-white/10">
        <motion.span
          className="absolute inset-x-0 h-4 bg-brand-400"
          animate={{ y: [-16, 48] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </span>
    </div>
  );
}

export default function Hero() {
  const resumeUrl = useResumeUrl();
  return (
    <section
      id="hero"
      className="min-h-viewport relative flex items-center overflow-hidden pb-16 pt-28 sm:pt-32 lg:pb-24"
    >
      <div className="shell w-full">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
          {/* ---------------- LEFT: the pitch ---------------- */}
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="text-fluid-3xl font-semibold leading-[1.02] tracking-tight text-slate-50"
            >
              I build{" "}
              <span className="inline-block">
                <RotatingWord />
              </span>
              <br className="hidden sm:block" />{" "}
              <span className="text-slate-400">that hold up in production.</span>
            </motion.h1>

            {/* The AEO answer paragraph. This exact text is also the
                `description` in JSON-LD and the meta description. */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="aeo-summary mt-7 max-w-prose2 text-fluid-base leading-relaxed text-slate-400"
            >
              <strong className="font-semibold text-slate-100">
                {person.name}
              </strong>{" "}
              is a Senior Full Stack Developer and Technical Engineering Lead in{" "}
              {person.location.city}, {person.location.region}, India, with{" "}
              <strong className="font-semibold text-slate-200">3+ years</strong>{" "}
              shipping production MERN applications. He leads a{" "}
              <strong className="font-semibold text-slate-200">
                10-engineer team
              </strong>{" "}
              at Avani Enterprises Ltd. and architected{" "}
              <strong className="font-semibold text-slate-200">
                Avani Business OS
              </strong>
              , a 25+ module multi-tenant platform used daily by 10,000+ users.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.22 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <a href="#projects" className="btn-primary group">
                <span className="font-semibold">See the work</span>
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
              </a>
              <a href="#contact" className="btn-ghost group">
                Start a conversation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href={resumeUrl}
                download="Soham-Dang-Resume.pdf"
                className="btn-ghost border-transparent bg-transparent px-3 text-slate-400 hover:bg-white/[0.04]"
              >
                Résumé (PDF)
              </a>
            </motion.div>

            {/* Location + focus line — GEO signal in visible copy */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.32 }}
              className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500"
            >
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-brand-400/70" />
                {person.location.city}, {person.location.region} · Delhi NCR ·
                Remote worldwide
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-accent-400/70" />
                MERN · AI Engineering · WebGL
              </span>
            </motion.div>
          </div>

          {/* ---------------- RIGHT: the credential card ---------------- */}
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <TiltCard intensity={7}>
              <div className="panel overflow-hidden rounded-4xl p-1.5">
                <div className="rounded-[1.7rem] border border-white/[0.06] bg-ink-950/70 p-5 sm:p-6">
                  {/* Identity row */}
                  <div className="flex items-center gap-4">
                    <div className="relative shrink-0">
                      <div className="absolute -inset-1 rounded-2xl bg-brand-gradient opacity-40 blur-md" />
                      <img
                        src={portrait}
                        alt="Soham Dang, Senior Full Stack Developer and Technical Engineering Lead"
                        width="64"
                        height="64"
                        loading="eager"
                        decoding="async"
                        className="relative h-16 w-16 rounded-2xl object-cover ring-1 ring-white/15"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-display text-lg font-semibold text-slate-50">
                        {person.name}
                      </p>
                      <p className="mt-0.5 text-xs leading-snug text-slate-400">
                        Senior Full Stack Developer
                        <span className="text-slate-600"> · </span>
                        Technical Engineering Lead
                      </p>
                      <p className="mt-1 font-mono text-[0.62rem] uppercase tracking-wider2 text-brand-300/70">
                        Avani Enterprises Ltd.
                      </p>
                    </div>
                  </div>

                  <div className="rule my-5" />

                  {/* Metrics — the four numbers a recruiter actually scans */}
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-5">
                    {stats.slice(0, 4).map((s) => (
                      <div key={s.label}>
                        <dt className="sr-only">{s.label}</dt>
                        <dd>
                          <div className="flex items-baseline gap-1">
                            <span className="gradient-text font-display text-2xl font-semibold leading-none">
                              {s.value}
                            </span>
                            <span className="font-mono text-[0.6rem] uppercase tracking-wider2 text-slate-600">
                              {s.unit}
                            </span>
                          </div>
                          <p className="mt-1.5 text-[0.72rem] leading-snug text-slate-400">
                            {s.label}
                          </p>
                        </dd>
                      </div>
                    ))}
                  </dl>

                  <div className="rule my-5" />

                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[0.6rem] uppercase tracking-label text-slate-600">
                      Currently building
                    </span>
                    <a
                      href="https://os.avanienterprises.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-1.5 text-xs text-brand-300 transition-colors hover:text-brand-200"
                    >
                      Avani Business OS
                      <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  </div>
                </div>
              </div>
            </TiltCard>
          </motion.div>
        </div>

        {/* Stack marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 lg:mt-24"
        >
          <p className="eyebrow mb-4">Daily stack</p>
          <Marquee items={HEADLINE_STACK} />
        </motion.div>
      </div>

      <ScrollCue />
    </section>
  );
}
